import create from 'zustand'
import { persist } from 'zustand/middleware'
import { ErrorStore, Error } from './types/error'
import { Loading, LoadingStore } from './types/loading'
import { AuthStore, AUTH_INITIAL_STATE } from './types/auth'
import { SuccessStore } from './types/success'
import { CCExpireStore } from './types/status'
import * as amplitude from '@amplitude/analytics-browser'
import {
    decodeToken,
    syncTokenToStorage,
    clearAuthStorage,
} from '../utils/token'
import { setExcelClientUserProperties } from '../utils/excel-client-tracking'

// Loading Store
export const useLoadingStore = create<LoadingStore>((set, get) => ({
    loading: { isLoading: false, message: '' },
    setLoading: (loading: Loading) => set({ loading }),
    abortController: null,
    setAbortController: (controller: AbortController | null) =>
        set({ abortController: controller }),
    cancelOperation: () => {
        const { abortController } = get()
        if (abortController) {
            abortController.abort()
            // Show cancelling message - the running code will clear loading when done
            set({
                loading: { isLoading: true, message: 'Cancelling...' },
            })
        }
    },
}))

// Error Store
export const useErrorStore = create<ErrorStore>((set) => ({
    error: { hasError: false, errorMessage: '' },
    setError: (error: Error) => set({ error }),
}))

// Success Store
export const useSuccessStore = create<SuccessStore>((set) => ({
    message: '',
    setSuccessMessage: (message: string) => set({ message }),
}))

// Auth Store with token management
export const useAuthStore = create(
    persist<AuthStore>(
        (set, get) => ({
            currentUser: AUTH_INITIAL_STATE,
            token: '',
            fmpApiKey: null,

            setAuth: (token: string) => {
                const decoded = decodeToken(token)

                if (!decoded) {
                    console.error('Failed to decode token')
                    return
                }

                syncTokenToStorage(token, get().fmpApiKey)

                set({
                    currentUser: decoded,
                    token,
                })

                amplitude.setUserId(decoded.email)

                if (process.env.NODE_ENV === 'production') {
                    amplitude.track('auth_success', {
                        userId: decoded.userId,
                        isSubscribed: decoded.isSubscribed,
                        subscriptionType: decoded.subscriptionType,
                    })

                    setExcelClientUserProperties()

                    try {
                        const { datadogLogs } = require('@datadog/browser-logs')
                        datadogLogs.setUser({
                            id: decoded.userId,
                            email: decoded.email,
                            subscription: decoded.subscriptionType || 'unknown',
                        })
                    } catch {}
                }
            },

            logout: () => {
                // Clear auth storage
                clearAuthStorage()

                set({
                    currentUser: AUTH_INITIAL_STATE,
                    token: '',
                    fmpApiKey: null,
                })

                // Clear amplitude user
                amplitude.reset()

                if (process.env.NODE_ENV === 'production') {
                    try {
                        const { datadogLogs } = require('@datadog/browser-logs')
                        datadogLogs.clearUser()
                    } catch {}
                }
            },

            setFMPApiKey: (fmpApiKey: string) => {
                syncTokenToStorage(get().token, fmpApiKey)
                set({ fmpApiKey })
            },
        }),
        {
            name: 'auth',
        }
    )
)

// CC Expire Store
export const useCCExpireStore = create<CCExpireStore>((set) => ({
    status: { expiring: false },
    setSubscriptionCCStatus: (status: boolean) =>
        set({ status: { expiring: status } }),
}))

// Nav Store
type NavStore = {
    isCollapsed: boolean
    toggleCollapsed: () => void
}
export const useNavStore = create<NavStore>()(
    persist(
        (set, get) => ({
            isCollapsed: false,
            toggleCollapsed: () => set({ isCollapsed: !get().isCollapsed }),
        }),
        { name: 'nav-collapsed' }
    )
)
